import { useEffect, useState } from "react";
import "../sign.css";
import Logo from "../../../ui/atoms/logo/Logo";
import LanguageSelector from "../../../ui/molecules/languages/LanguageSelector";
import MessageBox from "../../../ui/atoms/message-box/MessageBox";
import InputSelect from "../../../ui/atoms/inputs/input-select/InputSelect";
import InputText from "../../../ui/atoms/inputs/input-text/InputText";
import InputTextArea from "../../../ui/atoms/inputs/input-textarea/InputTextArea";
import Button from "../../../ui/atoms/buttons/Button";
import Loading from "../../../ui/atoms/loading/Loading";
import countries from "../../../app/config/countries.json";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
//
//
import { wait, isEmpty, capitalizeAll } from "../../../core/utils/helpers";
import { RegisterCompleteSchema } from "../../../core/utils/validators/authentication";
import { onCompleteInscription } from "../services/authentication";
import useAxiosPrivate from "../../../app/providers/context-provider/useAxiosPrivate";
//
import { onGetSchools, onGetStreams } from "../../admin/services/school";
import { logoutAndReset } from "../../../app/store/actions/authActions";
import Swal from "sweetalert2";
import moment from "moment";

// Calculate min and max dates
const minDate = moment("1960-01-01").format("YYYY-MM-DD");
const maxDate = moment().subtract(17, "years").format("YYYY-MM-DD");

const CompleteSignUp = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const axiosPrivate = useAxiosPrivate();

  const [isSending, setIsSending] = useState(false);
  const [isShowingMsgBox, setIsShowingMsgBox] = useState(false);
  const [messageData, setMessageData] = useState({ type: "", text: "" });

  const [countriesData, setCountriesData] = useState([]);

  const [schoolsData, setSchoolsData] = useState([]);
  const [streamsData, setStreamsData] = useState([]);

  const [schoolsArray, setSchoolsArray] = useState([]);
  const [streamsArray, setStreamsArray] = useState([]);

  const initConnectedUser = useSelector(
    (state) => state.setInitConf.initConnectedUser,
  );

  const userInformations = useSelector(
    (state) => state.setInitConf.userInformations,
  );

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onTouched",
    resolver: yupResolver(RegisterCompleteSchema),
    defaultValues: {
      user_id: userInformations?.id,
      firstname: userInformations?.firstname,
      lastname: userInformations?.lastname,
      mail: userInformations?.mail,
      inscription_id: initConnectedUser?.inscription?.inscription_id,
      program_language: "",
    },
  });

  const [selectedProgramLanguage, school] = watch([
    "program_language",
    "school",
  ]);

  useEffect(() => {
    const _countries = Array.from(countries, (item, _) => ({
      key: item?.name?.official,
      title: item?.name?.official,
      is_activated: false,
    }));

    setCountriesData(_countries);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      let isMounted = true;
      const controller = new AbortController();
      const signal = controller.signal;

      await onGetSchools(axiosPrivate, signal).then((response) => {
        const _data = response?.data?.result || [];
        setSchoolsData(_data);
      });

      await onGetStreams(axiosPrivate, signal).then((response) => {
        const _data = response?.data?.result || [];
        setStreamsData(_data);
      });

      return () => {
        isMounted = false;
        isMounted && controller.abort();
      };
    };
    fetchData();
  }, []);

  useEffect(() => {
    const onLoading = async () => {
      Swal.fire({
        title: "Loading Schools...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });
      //
      await wait(1000);
      //
      const tmpArray = schoolsData?.filter(
        (el) =>
          el?.school_is_activated &&
          el?.school_is_deleted === false &&
          el?.school_language === selectedProgramLanguage,
      );

      const _avalaibleSchools = Array.from(tmpArray, (item, _) => ({
        key: item?.school_id,
        title: capitalizeAll(item?.school_title),
        is_activated: false,
      }));

      setSchoolsArray(_avalaibleSchools);
      Swal.close();
    };
    onLoading();
  }, [selectedProgramLanguage]);

  useEffect(() => {
    const onLoading = async () => {
      Swal.fire({
        title: "Loading Streams...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });
      //
      await wait(1000);
      //
      const tmpArray = streamsData?.filter(
        (item) =>
          item?.school_id === school &&
          item?.classe_is_activated === true &&
          item?.classe_is_deleted === false,
      );

      const _avalaibleStreams = Array.from(tmpArray, (item, _) => ({
        key: item?.classe_id,
        title: capitalizeAll(item?.classe_title),
        is_activated: false,
      }));
      setStreamsArray(_avalaibleStreams);
      Swal.close();
    };
    onLoading();
  }, [school]);

  const onSubmit = async (data) => {
    setIsSending(true);
    await wait(700);
    //
    onCompleteInscription(axiosPrivate, data)
      .then((response) => {
        setIsSending(false);
        //
        const _data = response?.data;
        if (_data?.status) {
          setIsShowingMsgBox(true);
          setMessageData({ type: "success", text: _data?.message });
          reset();

          dispatch(logoutAndReset());

          const timer = setTimeout(() => {
            setIsShowingMsgBox(false);
            setMessageData({ type: "", text: "" });

            navigate("/signin", { replace: true });
          }, 2000);
          return () => clearTimeout(timer);
        } else {
          setIsShowingMsgBox(true);
          setMessageData({ type: "warning", text: _data?.message });
          //
          const timer = setTimeout(() => {
            setIsShowingMsgBox(false);
            setMessageData({ type: "", text: "" });
          }, 3000);
          return () => clearTimeout(timer);
        }
      })
      .catch((error) => {
        setIsSending(false);
        setIsShowingMsgBox(true);
        if (!error?.response) {
          setMessageData({ type: "info", text: "No server response" });
        } else {
          setMessageData({
            type: "warning",
            text: error?.response?.data?.detail?.message,
          });
        }
        const timer = setTimeout(() => {
          setIsShowingMsgBox(false);
          setMessageData({ type: "", text: "" });
        }, 2000);
        return () => clearTimeout(timer);
      });
  };

  return (
    <div className="w-screen h-screen position-relative bg-primary d-flex">
      <div className="w-80 h-full sign-section position-relative bg-white p-sm m-auto">
        <div className="sign-head w-full d-flex justify-space-between align-center">
          <Logo to={null} width={"100px"} />
          <div className="w-fit-content">
            <LanguageSelector />
          </div>
        </div>
        <form
          className="sign-body w-full d-flex justify-center align-center flex-col pl-lg pr-lg"
          onSubmit={handleSubmit(onSubmit)}
        >
          <h1 className="mb-lg">{t("sign.sign_up_complete")}</h1>
          {isShowingMsgBox && (
            <div className="w-full mb-xs">
              <MessageBox type={messageData?.type} msg={messageData?.text} />
            </div>
          )}
          <div className="w-full d-flex justify-space-between align-center mt-xs gap-xs">
            <div className="w-50">
              <InputSelect
                register={register}
                name={"program_language"}
                placeholder={t("user.program_language")}
                data={[
                  {
                    key: "en",
                    title: "English",
                    is_activated: false,
                  },
                  {
                    key: "fr",
                    title: "French",
                    is_activated: false,
                  },
                ]}
                error={errors?.program_language?.message}
              />
            </div>
            <div className="w-50">
              <InputText
                type={"text"}
                register={register}
                name="register_code"
                title={t("sign.sign_up_code")}
                error={errors?.register_code?.message}
              />
            </div>
          </div>
          <div className="w-full d-flex justify-space-between align-center mt-xs gap-xs">
            <div className="w-50">
              <InputSelect
                register={register}
                name={"school"}
                placeholder={"School of Interest"}
                data={schoolsArray}
                error={errors?.school?.message}
              />
            </div>
            <div className="w-50">
              <InputSelect
                register={register}
                name={"stream"}
                placeholder={"Stream (Class) of Interest"}
                data={streamsArray}
                error={errors?.stream?.message}
              />
            </div>
          </div>
          <div className="w-full d-flex justify-space-between align-center mt-xs gap-xs">
            <div className="w-50">
              <InputText
                type={"text"}
                register={register}
                name="firstname"
                title={t("user.firstname")}
                error={errors?.firstname?.message}
              />
            </div>
            <div className="w-50">
              <InputText
                type={"text"}
                register={register}
                name="lastname"
                title={t("user.lastname")}
                error={errors?.lastname?.message}
              />
            </div>
          </div>
          <div className="w-full d-flex justify-space-between align-center mt-xs gap-xs">
            <div className="w-50">
              <InputSelect
                register={register}
                name={"gender"}
                placeholder={"Select gender"}
                data={[
                  { key: "female", title: "Female", is_activated: false },
                  { key: "male", title: "Male", is_activated: false },
                ]}
                error={errors?.gender?.message}
              />
            </div>
            <div className="w-50">
              <InputText
                type={"text"}
                register={register}
                name="mail"
                title={t("user.email")}
                disabled={true}
                error={errors?.mail?.message}
              />
            </div>
          </div>
          <div className="w-full d-flex justify-space-between align-center mt-xs gap-xs">
            <div className="w-50">
              <InputText
                type={"date"}
                register={register}
                name="birth"
                title={t("user.birth")}
                error={errors?.birth?.message}
                min={minDate}
                max={maxDate}
              />
            </div>
            <div className="w-50">
              <InputSelect
                register={register}
                name={"birth_location"}
                placeholder={t("user.birth_location")}
                data={countriesData}
                error={errors?.birth_location?.message}
              />
            </div>
          </div>
          <div className="w-full d-flex justify-space-between align-center mt-xs gap-xs">
            <div className="w-50">
              <InputSelect
                register={register}
                name={"origin_country"}
                placeholder={t("user.origin_country")}
                data={countriesData}
                error={errors?.origin_country?.message}
              />
            </div>
            <div className="w-50">
              <InputSelect
                register={register}
                name={"profession"}
                placeholder={t("user.profession")}
                data={[
                  {
                    key: "student",
                    title: "Student",
                    is_activated: false,
                  },
                  {
                    key: "employee",
                    title: "Employee",
                    is_activated: false,
                  },
                  {
                    key: "researcher",
                    title: "Researcher",
                    is_activated: false,
                  },
                  {
                    key: "other",
                    title: "Other",
                    is_activated: false,
                  },
                ]}
                error={errors?.profession?.message}
              />
            </div>
          </div>
          <div className="w-full mt-xs">
            <InputTextArea
              type={"text"}
              register={register}
              name="expectations"
              title={t("user.expectations")}
              error={errors?.expectations?.message}
            />
          </div>
          <div className="w-full d-flex flex-col align-center mt-xs">
            <p>
              Agree to the <Link to={""}>Terms and Conditions</Link> of using
              the platform.
            </p>
            <InputText
              type={"checkbox"}
              register={register}
              name="agreement"
              title={""}
              error={
                !isEmpty(errors?.agreement?.message) && t("user.agreement")
              }
            />
          </div>
          <div className="w-fit-content mt-md">
            {isSending ? (
              <Loading type={"type-1"} />
            ) : (
              <Button
                type={"submit"}
                preferedStyle={"normal"}
                title={t("sign.sign_up_complete_button")}
                action={null}
              />
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default CompleteSignUp;
